// The questions on /pricing. One list feeds the visible FAQ and the FAQPage
// structured data, and it follows the public state of the free plan: the same
// answer must never promise a plan that is full or closed.
import { FREE_PLAN, FREE_PLAN_EXCLUDES, type FreePlanState } from '@/lib/free-plan'
import { FREE_DOMAIN_RULE, PAID_ANY_EMAIL } from '@/lib/free-domain'
import { WORK_EMAIL_FIX } from '@/lib/account-emails'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'
import { THIRD_PARTY_PUBLIC } from '@/lib/third-party'
import type { FaqEntry } from '@/lib/schema'
import { NONPROFIT_FAQ } from '@/lib/nonprofit'
import { THIRD_PARTY_PRICING_FAQ } from '@/components/third-party-marketing/public-copy'


/** What the free plan has, in one paragraph. Numbers come from `FREE_PLAN`. */
export function freePlanSummary(): string {
  return (
    `${FREE_PLAN.monitors} monitors checked every ${FREE_PLAN.intervalSeconds} seconds from one location, ${FREE_PLAN.location}. ` +
    'It covers HTTP and keyword monitors, alerts by email and one chat channel (Discord, Slack or Telegram), ' +
    `${FREE_PLAN.statusPages} status page, ${FREE_PLAN.teamMembers} team member and ${FREE_PLAN.historyDays} days of history. ` +
    'Commercial use is allowed.'
  )
}

const WHERE_PAID = MONITOR_LOCATIONS.plan.faqWhere

export function pricingFaqs(state: FreePlanState): FaqEntry[] {
  const open = state === 'open'
  const full = state === 'full'
  const hasFree = open || full

  const freeAnswer = open
    ? `Yes. The free plan has ${freePlanSummary()} It does not include ${FREE_PLAN_EXCLUDES}. The number of free accounts is limited. When the limit is reached, new sign-ups see "Free plan is full" and can choose a paid plan instead.`
    : full
      ? `Yes, but it is full right now, so new accounts cannot join it at the moment. It has ${freePlanSummary()} It does not include ${FREE_PLAN_EXCLUDES}. Paid plans start at $3/mo for 50 monitors, all checked every 30 seconds.`
      : 'Not at the moment. Paid plans start at $3/mo for 50 monitors, all checked every 30 seconds, with a 30-day money-back guarantee.'

  const faqs: FaqEntry[] = [
    { question: 'Is there a free plan?', answer: freeAnswer },
    { question: NONPROFIT_FAQ.question, answer: NONPROFIT_FAQ.answer },
  ]
  if (hasFree) {
    faqs.push({
      question: 'What does the free plan not include?',
      answer:
        `${FREE_PLAN_EXCLUDES[0].toUpperCase()}${FREE_PLAN_EXCLUDES.slice(1)}. ` +
        MONITOR_LOCATIONS.plan.faqNotIncluded,
    })
  }
  if (hasFree) {
    faqs.push({
      question: 'Who can use the free plan?',
      answer:
        `${FREE_DOMAIN_RULE} You can then monitor any site. ${WORK_EMAIL_FIX} ${PAID_ANY_EMAIL}`,
    })
  }
  faqs.push(
    {
      question: 'Do I need a subscription?',
      answer: open
        ? 'No. The free plan needs no subscription and no credit card. Pro, Team and Business add more monitors, more locations and more features.'
        : full
          ? 'For now, yes: the free plan is full. Choose Pro, Team, or Business to use monitoring and alerts. You can manage your account and billing before subscribing.'
          : 'Yes. Choose Pro, Team, or Business to use monitoring and alerts. You can manage your account and billing before subscribing.',
    },
    {
      question: 'What makes Uptrack different from UptimeRobot?',
      answer:
        'Alert confirmation. Most monitoring tools alert on the first failed check — a single DNS timeout at 3am wakes you up for nothing. Uptrack requires consecutive failed checks before alerting, so you only hear about real incidents.',
    },
    {
      question: 'Where do you check from?',
      answer: hasFree
        ? `${WHERE_PAID} Several locations must confirm a failure before a paid plan alerts you. The free plan checks from one location, ${FREE_PLAN.location}, and still waits for consecutive failed checks before it alerts.`
        : `${WHERE_PAID} Several locations must confirm a failure before you get an alert.`,
    },
    {
      question: 'Can I change plans later?',
      answer: 'Yes. Upgrade or downgrade anytime. Changes take effect immediately and billing is prorated.',
    },
    {
      question: 'Is there a free trial?',
      answer: hasFree
        ? 'The paid plans have no timed trial. The free plan is not a trial, and every paid plan has a 30-day money-back guarantee. If Uptrack is not right for you, email hello@uptrack.app within 30 days for a full refund.'
        : 'No trial, but every plan has a 30-day money-back guarantee. If Uptrack is not right for you, email hello@uptrack.app within 30 days for a full refund.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit cards through our payment partner. All billing and tax compliance is handled securely as a merchant of record.',
    },
    {
      question: 'This is missing features I need. What should I do?',
      answer:
        'Tell us. Uptrack is actively developed and we prioritize based on real user needs. Email us at hello@uptrack.app or open an issue on GitHub.',
    },
  )
  // The third party question shows only when THIRD_PARTY_PUBLIC is true (src/lib/third-party.ts).
  if (THIRD_PARTY_PUBLIC) {
    const at = faqs.findIndex((f) => f.question === 'What payment methods do you accept?')
    faqs.splice(at < 0 ? faqs.length : at, 0, THIRD_PARTY_PRICING_FAQ)
  }
  return faqs
}
